/**
 * Browser copy of the plan-switch helpers the app renders.
 * Netlify loads src/shared/planSwitch.js through CommonJS. This file stays
 * ESM and does not load that module, so the production bundle does not
 * assign CommonJS exports inside an ES module.
 */

const TEAM_ENDED_NOTICE = 'Your team plan ended. Pick your own plan to keep going; your clients and data are safe.';

function hadTeamAccess(profile) {
  if (!profile) return false;
  if (profile.had_team_access === true) return true;
  return Boolean(profile.prior_team_id);
}

const RETURNING_SUBSCRIPTION_STATUSES = new Set([
  'active',
  'past_due',
  'canceled',
  'cancelled',
  'unpaid',
  'incomplete_expired',
]);

/** Same rule as planSwitch.checkoutTrialDays. Null means no free trial. */
function checkoutTrialDays(profile) {
  if (hadTeamAccess(profile)) return null;
  if (profile && profile.trial_ends_at) return null;
  const status = String((profile && profile.subscription_status) || '').toLowerCase();
  if (RETURNING_SUBSCRIPTION_STATUSES.has(status)) return null;
  return 14;
}

/** Smallest team price. Solo is a separate action, not a seat step. */
const TEAM_MIN_AGENTS = 2;
const SOLO_CONFIRM_ERROR = 'Switching to Solo needs confirmation.';

function occupiedSeats(currentSeats, pendingInvites, memberCount) {
  const used = Number(currentSeats);
  const pending = Number(pendingInvites) || 0;
  const seated = Number.isFinite(used) ? used : 0;
  const listed = Number(memberCount);
  const people = Number.isFinite(listed) ? Math.max(seated, listed) : seated;
  return people + pending;
}

function emptySeatCount(team, pendingInvites) {
  if (!team) return 0;
  const max = Number(team.max_seats);
  const used = Number(team.current_seats);
  const pending = Number(pendingInvites) || 0;
  if (!Number.isFinite(max) || !Number.isFinite(used)) return 0;
  return Math.max(0, max - used - pending);
}

function minimumSeatCount(team, pendingInvites, memberCount) {
  const occupied = occupiedSeats(team && team.current_seats, pendingInvites, memberCount);
  return Math.max(TEAM_MIN_AGENTS, occupied);
}

function nextLowerSeatCount(team, pendingInvites, memberCount) {
  const max = Number(team && team.max_seats);
  if (!Number.isFinite(max)) return null;
  const next = Math.floor(max) - 1;
  if (next < minimumSeatCount(team, pendingInvites, memberCount)) return null;
  return next;
}

function emptySeatCopy(count) {
  const seats = Number(count) || 0;
  if (seats === 1) return 'You have 1 empty seat';
  return `You have ${seats} empty seats`;
}

function seatChangeAllowed({ nextAgents, currentSeats, pendingInvites }) {
  const next = Number(nextAgents);
  const used = Number(currentSeats) || 0;
  const pending = Number(pendingInvites) || 0;
  const occupied = used + pending;
  if (!Number.isFinite(next)) {
    return { ok: false, error: 'That seat count is not available.' };
  }
  if (next < TEAM_MIN_AGENTS) {
    return {
      ok: false,
      error: 'Seat changes stay on a team plan. Switch to Solo from the danger zone.',
    };
  }
  if (next < occupied) {
    const floor = Math.max(1, occupied);
    return {
      ok: false,
      error: `You can't reduce below ${floor} seats. Cancel an invite or remove a member first.`,
    };
  }
  return { ok: true };
}

function seatFloorMessage(team, pendingInvites, memberCount) {
  if (!team) return '';
  const floor = minimumSeatCount(team, pendingInvites, memberCount);
  const max = Number(team.max_seats);
  const abovePeople = floor > TEAM_MIN_AGENTS;
  const atFloor = !Number.isFinite(max) || max <= floor;
  if (!atFloor) {
    if (!abovePeople) return '';
    return `You can't reduce below ${floor} seats. Cancel an invite or remove a member first.`;
  }
  if (abovePeople) {
    return `You can't reduce below ${floor} seats. Cancel an invite or remove a member first.`;
  }
  return 'Seat changes stay on a team plan. Switch to Solo from the danger zone.';
}

function seatReductionMessage({ fromAgents, toAgents, fromPrice, toPrice, addonLabel }) {
  const from = Number(fromAgents);
  const to = Number(toAgents);
  const fromAmount = Number(fromPrice);
  const toAmount = Number(toPrice);
  const included = addonLabel ? `, including ${addonLabel}` : '';
  const price = Number.isFinite(fromAmount) && Number.isFinite(toAmount)
    ? ` The price changes from $${fromAmount}/month to $${toAmount}/month${included}.`
    : '';
  return `Your team goes from ${from} seats to ${to} seats.${price} Billing is prorated.`;
}

function isSoloDowngrade(plan) {
  if (!plan) return false;
  if (plan.planName === 'solo') return true;
  const agents = Number(plan.agents);
  return Number.isFinite(agents) && agents < TEAM_MIN_AGENTS;
}

function soloDowngradeBlock(plan, confirmSolo) {
  if (!isSoloDowngrade(plan)) return null;
  if (confirmSolo === true) return null;
  return { status: 409, error: SOLO_CONFIRM_ERROR };
}

function agentsRemovedBySolo(members, ownerId) {
  return (members || []).filter((member) => {
    if (!member || member.id === ownerId) return false;
    if (member.is_team_owner === true || member.role === 'owner') return false;
    return true;
  }).map((member) => {
    const name = [member.first_name, member.last_name]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(' ');
    const email = String(member.email || '').trim();
    let label = 'A team member';
    if (name && email) label = `${name} (${email})`;
    else if (name || email) label = name || email;
    return { id: member.id, label };
  });
}

function addonRemovalLine(addon) {
  const id = String(addon || 'none').trim().toLowerCase();
  if (id === 'plus') return 'The Plus homeowner add-on goes away.';
  if (id === 'max') return 'The Max homeowner add-on goes away.';
  return 'There is no homeowner add-on on this team.';
}

function soloGraceLine(days) {
  const count = Number(days);
  const shown = Number.isFinite(count) && count >= 0 ? count : 30;
  return `For ${shown === 1 ? '1 day' : `${shown} days`} after you switch, your homeowner limit stays at the homeowners you already have, and never below the Solo limit of 200. After that, the Solo limit applies. Existing homeowners keep access.`;
}

function teamConfirmName(name) {
  const trimmed = String(name || '').trim();
  return trimmed || 'Your Team';
}

function teamNameMatches(expected, typed) {
  const name = teamConfirmName(expected);
  return name === String(typed || '').trim();
}

function planPickerCopy(profile) {
  const ended = Boolean(profile && profile.team_plan_ended === true);
  const prior = hadTeamAccess(profile);
  const eligible = checkoutTrialDays(profile) === 14;
  return {
    intro: ended
      ? TEAM_ENDED_NOTICE
      : (eligible ? 'Your account has been created successfully.' : ''),
    soloLabel: prior ? 'Choose Solo' : 'Start Solo Agent Trial',
    teamLabel: (size) => (prior ? `Choose ${size}-Agent Team` : `Start ${size}-Agent Team Trial`),
  };
}

export {
  TEAM_MIN_AGENTS,
  SOLO_CONFIRM_ERROR,
  hadTeamAccess,
  checkoutTrialDays,
  planPickerCopy,
  seatFloorMessage,
  seatChangeAllowed,
  seatReductionMessage,
  emptySeatCount,
  emptySeatCopy,
  minimumSeatCount,
  nextLowerSeatCount,
  isSoloDowngrade,
  soloDowngradeBlock,
  agentsRemovedBySolo,
  addonRemovalLine,
  soloGraceLine,
  teamConfirmName,
  teamNameMatches,
};
